import axios from "axios";
import hash from "object-hash";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { apiClientsAuthHeaders } from "./auth";
import { queryOptions } from "./utils";

const API_URL = "https://d3ec9b5jthpueq.cloudfront.net";

// ************************************************
//       Buildings - Geo location of Buildings
// ************************************************
interface Bounds {
  north: number;
  south: number;
  west: number;
  east: number;
}

function getBounds(mapBounds: any): Bounds {
  if (mapBounds === undefined) return { north: 0, south: 0, west: 0, east: 0 };
  return {
    north: mapBounds.getNorth(),
    south: mapBounds.getSouth(),
    west: mapBounds.getWest(),
    east: mapBounds.getEast(),
  };
}

async function getBuildingsByBounds(
  north: number,
  south: number,
  west: number,
  east: number
): Promise<Record<string, any>> {
  const res = await axios.get(
    `${API_URL}/buildings/bounds_postgis/${north}/${south}/${west}/${east}`,
    { headers: await apiClientsAuthHeaders() }
  );
  const hashMap: Record<string, any> = {};
  res.data.buildings.forEach((building: any) => {
    const key = hash(JSON.stringify(building.geometry.coordinates));
    hashMap[key] = { ...building, key };
  });
  return hashMap;
}

export const useBuildingsByBounds = (
  mapBounds: any
): UseQueryResult<Record<string, any>> => {
  const { north, south, west, east } = getBounds(mapBounds);

  let mapSizeMeters = 100_000;
  if (mapBounds !== undefined)
    mapSizeMeters =
      mapBounds.getNorthEast().distanceTo(mapBounds.getSouthWest()) || 100_000;

  const data = useQuery(
    ["buildings_by_bounds", north, south, west, east],
    async () => {
      return await getBuildingsByBounds(north, south, west, east);
    },
    queryOptions({ enabled: !!mapBounds && mapSizeMeters < 7000 })
  );
  return data;
};

// ************************************************
//       Deeds - Ownership of land
// ************************************************

async function getTitlesByBounds(
  north: number,
  south: number,
  west: number,
  east: number
): Promise<Record<string, any>> {
  const res = await axios.get(
    `${API_URL}/land_registrations/bounds_postgis/${north}/${south}/${west}/${east}`,
    { headers: await apiClientsAuthHeaders() }
  );
  const hashMap: Record<string, any> = {};
  res.data.land_registrations.forEach((building: any) => {
    building.isTitleDeed = true;
    const key = hash(building.geometry.coordinates);
    hashMap[key] = { ...building };
  });
  return hashMap;
}

export const useDeedsByBounds = (
  mapBounds: any
): UseQueryResult<Record<string, any>> => {
  const { north, south, west, east } = getBounds(mapBounds);

  const data = useQuery(
    ["deeds_by_bounds", north, south, west, east],
    async () => {
      return await getTitlesByBounds(north, south, west, east);
    },
    queryOptions({ enabled: !!mapBounds })
  );
  return data;
};
